import React, { useRef, useState, useEffect, useContext, useLayoutEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import LinkIcon from '@atlaskit/icon/core/link';
import Button from '@atlaskit/button/button';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { END } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { COPY_CONTENT_LINK_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceTrackerContext,
	ExperienceSuccess,
	ExperienceStop,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { fg } from '@confluence/feature-gating';

import { useCopyContentLink } from './useCopyContentLink.ts';
import { ACTION_SUBJECT_ID } from './CopyLinkConstants.ts';
import type { CopyLinkProps } from './CopyLinkConstants.ts';

const i18n = defineMessages({
	copyLinkLabel: {
		id: 'share-and-restrict-dialog.copylink.icon.label',
		defaultMessage: 'Copy link',
		description: 'Label and tooltip for the button to copy content URL',
	},
	copiedTextLabel: {
		id: 'share-and-restrict-dialog.copylink.copied.tooltip',
		defaultMessage: 'Copied!',
		description:
			'Text for the copy link button tooltip when content URL has been successfully copied',
	},
	errorCopyingTextLabel: {
		id: 'share-and-restrict-dialog.copylink.error.tooltip',
		defaultMessage: 'Something went wrong.',
		description:
			'Text for the copy link button tooltip when an error occurred while trying to copy link',
	},
});

const RESET_COPYTEXT_TIMEOUT = 5000;

const CopyLinkComponent: React.FC<CopyLinkProps> = ({
	pageMode,
	contentType,
	contentId,
	spaceKey,
	source,
	shouldResetOnHover = false,
	buttonAppearance = 'subtle',
	tooltipPosition = 'bottom',
	referencedContentId,
}) => {
	const intl = useIntl();
	const [isCopied, setIsCopied] = useState<boolean>(false);
	const [errorDuringCopy, setErrorDuringCopy] = useState<Error | undefined>();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const cleanupCopiedTimer = useRef<NodeJS.Timeout>();
	const updateTooltipPosition = React.useRef<() => void>();
	const experienceTracker = useContext(ExperienceTrackerContext);

	useEffect(() => {
		if (isCopied) {
			cleanupCopiedTimer.current = setTimeout(() => setIsCopied(false), RESET_COPYTEXT_TIMEOUT);
		}
		return () => {
			clearTimeout(cleanupCopiedTimer.current);
		};
	}, [isCopied]);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				actionSubject: 'copyShareLink',
				action: 'shown',
				source,
				attributes: {
					contentType,
					pageMode,
					isContentWrapper: fg('confluence_frontend_content_wrapper'),
				},
			},
		}).fire();
	}, [createAnalyticsEvent, contentType, source, pageMode]);

	const { queryError, unsupportedLinkError, copyContentLink } = useCopyContentLink({
		pageMode,
		contentType,
		contentId,
		spaceKey,
		source,
	});

	useLayoutEffect(() => {
		updateTooltipPosition.current?.();
	}, [isCopied]);

	const handleCopyLink = async () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: ACTION_SUBJECT_ID,
				source,
				attributes: {
					pageMode,
					contentType,
					contentId,
					navdexPointType: END,
					isContentWrapper: fg('confluence_frontend_content_wrapper'),
				},
			},
		}).fire();

		try {
			// In an iframe, Chrome requires the iframe's window to be focused for clipboard write.
			// The click focuses the iframe, but by the time the async copy runs that focus can be
			// lost. Calling focus() here (same sync turn as the click) keeps this window focused
			// so the subsequent clipboard call is allowed. Gated by cc_fix_copy_in_in_embedded_confluence.
			if (
				typeof window !== 'undefined' &&
				window.self !== window.top &&
				fg('cc_fix_copy_in_in_embedded_confluence')
			) {
				window.focus();
			}
			await copyContentLink();
		} catch (e) {
			setErrorDuringCopy(e);
		}
		setIsCopied(true);
	};

	const handleHover = () => {
		experienceTracker.start({
			name: COPY_CONTENT_LINK_EXPERIENCE,
		});
	};

	// report errors from the hook's query silently since we have fallback link retrieval
	// methods, but show user error tooltip text and fail experience if we actually
	// couldn't get any link to copy or the copy itself failed
	const copyError = unsupportedLinkError || errorDuringCopy;
	const copyLinkText = intl.formatMessage(i18n.copyLinkLabel);
	const confirmationText = intl.formatMessage(
		copyError ? i18n.errorCopyingTextLabel : i18n.copiedTextLabel,
	);
	const isAnyError = Boolean(queryError || copyError);
	const buttonAriaProps = fg('confluence_title_copy_link_a11y_fix')
		? { 'aria-labelledby': referencedContentId }
		: { 'aria-label': isCopied ? confirmationText : copyLinkText };

	return (
		<>
			{isAnyError && <ErrorDisplay error={queryError || (copyError as Error)} />}
			{Boolean(copyError) && (
				<ExperienceStop name={COPY_CONTENT_LINK_EXPERIENCE} error={copyError} />
			)}
			<Tooltip
				content={({ update }) => {
					updateTooltipPosition.current = update;
					return isCopied ? confirmationText : copyLinkText;
				}}
				position={tooltipPosition}
			>
				<Button
					testId="copy-link-button"
					{...buttonAriaProps}
					appearance={buttonAppearance}
					iconBefore={
						<LinkIcon
							label=""
							color={isCopied ? token('color.icon.selected') : undefined}
							spacing="spacious"
						/>
					}
					onClick={handleCopyLink}
					onMouseEnter={handleHover}
					// Some consumers of CopyLink only reveal the button on hover, and
					// for those ones we need to reset the button to default post hover.
					// state after mouse leaves the component (because the button gets
					// hidden when no longer hovering)
					onMouseLeave={() => {
						if (shouldResetOnHover) setIsCopied(false);
						experienceTracker.abort({
							name: COPY_CONTENT_LINK_EXPERIENCE,
							reason: 'user navigated away',
						});
					}}
					shouldFitContainer
				/>
			</Tooltip>
			{isCopied && <ExperienceSuccess name={COPY_CONTENT_LINK_EXPERIENCE} />}
		</>
	);
};

export const CopyLink = withErrorBoundary({
	attribution: Attribution.PERMISSIONS_EXPERIENCE,
})(CopyLinkComponent);
